import { getAvatarChoice, setAvatarChoice } from "@/hooks/useAvatarChoice";
import {
  ANIMAL_IDS,
  COLOURS,
  type AnimalId,
  type ColourId,
} from "@/components/camp/campData";
import {
  resolveAvatarColor,
  resolveAvatarEmoji,
  type AvatarChoice,
} from "@/engine/state/avatarChoice";
import { AVATAR_COLORS, AVATAR_EMOJIS } from "@/engine/state/avatarEmoji";
import { suggestNickname } from "@/engine/state/nickname";
import { setSuggestedDisplayName } from "@/hooks/useDisplayName";

/**
 * A person's camp identity — a brandbook animal and a brandbook colour — read
 * from and written back to the emoji and hex the room already stores and
 * sends.
 *
 * The wire keeps the old spelling on purpose: a peer on an older build reads
 * the emoji and hex it always did, and a new build reads the same two values
 * as an animal and a colour. Each animal owns one emoji and each colour one
 * hex, so a choice made here survives the round trip exactly; any other emoji
 * or hex (an old choice) lands deterministically on one of the camp's own.
 *
 * At the seam, because the vocabulary belongs to the design system.
 */
export const EMOJI_FOR_ANIMAL: Readonly<Record<AnimalId, string>> = {
  fox: "🦊",
  owl: "🦉",
  bear: "🐻",
  hare: "🐰",
  wolf: "🐺",
  turtle: "🐢",
  deer: "🦄",
  cat: "🐱",
  frog: "🐸",
  hedge: "🐹",
  mouse: "🐭",
  raccoon: "🦝",
};

export const COLOUR_IDS: readonly ColourId[] = COLOURS.map(([id]) => id);

function hexForColour(colour: ColourId): string {
  return AVATAR_COLORS[COLOUR_IDS.indexOf(colour)] ?? AVATAR_COLORS[0];
}

export function animalForEmoji(emoji: string): AnimalId {
  const own = ANIMAL_IDS.find((id) => EMOJI_FOR_ANIMAL[id] === emoji);
  if (own) return own;
  const index = Math.max(0, AVATAR_EMOJIS.indexOf(emoji));
  return ANIMAL_IDS[index % ANIMAL_IDS.length] ?? "fox";
}

export function colourForHex(hex: string): ColourId {
  const index = Math.max(0, AVATAR_COLORS.indexOf(hex.toUpperCase()));
  return COLOUR_IDS[index % COLOUR_IDS.length] ?? "rust";
}

export interface CampIdentity {
  animal: AnimalId;
  colour: ColourId;
}

/** What this person looks like at the camp, chosen or defaulted per peer. */
export function campIdentityFor(
  peerId: string,
  choice: AvatarChoice,
): CampIdentity {
  return {
    animal: animalForEmoji(resolveAvatarEmoji(peerId, choice.emoji)),
    colour: colourForHex(resolveAvatarColor(peerId, choice.color)),
  };
}

/** The stored choice that says this animal and this colour. */
export function choiceForCamp(identity: CampIdentity): AvatarChoice {
  return {
    emoji: EMOJI_FOR_ANIMAL[identity.animal],
    color: hexForColour(identity.colour),
  };
}

/** A different animal and colour from the camp's own lists. */
export function randomCampIdentity(
  current: CampIdentity,
  random: () => number = Math.random,
): CampIdentity {
  const pick = <T>(list: readonly T[], exclude: T): T => {
    const options = list.filter((item) => item !== exclude);
    return options[
      Math.min(options.length - 1, Math.floor(random() * options.length))
    ];
  };
  return {
    animal: pick(ANIMAL_IDS, current.animal),
    colour: pick(COLOUR_IDS, current.colour),
  };
}

/**
 * Settles the stored choice on a camp identity: the animal and colour the
 * library draws, written back as the emoji and hex the room sends. A visitor
 * with nothing stored gets their deterministic default as an animal; one whose
 * stored emoji has no figure of its own gets the animal it is already drawn as,
 * so the picture, the name suggested from it and the room agree.
 */
export function ensureCampChoice(peerId: string): void {
  if (!peerId) return;
  const current = getAvatarChoice();
  const settled = choiceForCamp(campIdentityFor(peerId, current));
  if (current.emoji === settled.emoji && current.color === settled.color)
    return;
  setAvatarChoice(settled);
}

/**
 * A different camp animal and colour for this visitor, and the nickname that
 * follows them — a suggested name belongs to the character, so a shuffled
 * animal does not keep the old animal's name. A typed name is kept, because
 * `setSuggestedDisplayName` leaves a custom one alone.
 */
export function shuffleCampCharacter(peerId: string): void {
  const next = choiceForCamp(
    randomCampIdentity(campIdentityFor(peerId, getAvatarChoice())),
  );
  setSuggestedDisplayName(suggestNickname(peerId, next));
  setAvatarChoice(next);
}
